import FakeInput from 'components/Common/Form/Input/FakeInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import DropdownList from 'components/Luxkit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'components/Luxkit/Icons/line/LineAngleDownIcon'
import Modal from 'components/Luxkit/Modal/Modal'
import { INTL_SHORT_MONTH, INTL_SHORT_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { rem } from 'polished'
import React, { ComponentProps, forwardRef, ReactElement, useMemo, useRef } from 'react'
import styled from 'styled-components'
import MonthPicker from './MonthPicker'

const DropdownContainer = styled.div`
  padding-inline: ${rem(16)};
`

type InheritedDropdownListProps = Partial<Pick<ComponentProps<typeof DropdownList>, 'anchorRef'>>
type InheritedDropdownTextInputProps = Pick<ComponentProps<typeof TextInput>, 'disabled' | 'required'>
type InheritedMonthPickerProps = ComponentProps<typeof MonthPicker>

interface Props extends InheritedDropdownListProps, InheritedDropdownTextInputProps, InheritedMonthPickerProps {
  name?: string
  label?: string | ReactElement
  placeholder?: string
  optionalIndicatorEnabled?: boolean
}

const MonthPickerSelectInput = forwardRef<HTMLInputElement, Props>(function MonthPickerSelectInput(
  {
    name,
    label,
    placeholder,
    optionalIndicatorEnabled,
    anchorRef,
    disabled,
    required,
    value,
    ...monthPickerProps
  },
  ref,
) {
  const fakeInputRef = useRef<HTMLLabelElement>(null)
  const { value: isOpen, toggle: toggleOpen, off: close } = useToggle(false)

  const isMobile = useScreenSizeOnly('mobile')

  const format = useIntlDateFormatter()
  const formattedValue = useMemo<string>(() => {
    if (!value.size) return ''
    return Array.from(groupBy(
      Array.from(value),
      v => Number(v.split('-')[0]),
      v => Number(v.split('-')[1]),
    ).entries())
      .sort(([yearA], [yearB]) => yearA - yearB)
      .flatMap(([year, months]) => {
        return sortBy(months, v => v, 'asc').map((month, index) => {
          if (index === months.length - 1) {
            return format(new Date(year, month - 1), INTL_SHORT_MONTH_LONG_YEAR)
          } else {
            return format(new Date(year, month - 1), INTL_SHORT_MONTH)
          }
        })
      })
      .join(', ')
  }, [value, format])

  const serialisedValue = useMemo(() => {
    return Array.from(value.values()).join(',')
  }, [value])

  const picker = <MonthPicker {...monthPickerProps} value={value} />

  return <>
    <FakeInput
      ref={fakeInputRef}
      label={label}
      value={formattedValue}
      required={required}
      placeholder={placeholder}
      disabled={disabled}
      endIcon={<LineAngleDownIcon />}
      onClick={toggleOpen}
    />
    <HiddenInput ref={ref} name={name} value={serialisedValue} />
    <DropdownList
      size="fill-anchor"
      open={isOpen && !isMobile}
      anchorRef={anchorRef ?? fakeInputRef}
      triggerRef={fakeInputRef}
      onClose={close}
    >
      <DropdownContainer>
        {picker}
      </DropdownContainer>
    </DropdownList>
    <Modal
      title={label ?? ''}
      size="S"
      primaryActionText="Select"
      onPrimaryActionClick={close}
      actionsLayout="stacked"
      isOpen={isOpen && isMobile}
      onClose={close}
    >
      {picker}
    </Modal>
  </>
})

export default MonthPickerSelectInput
